import { useEffect, useRef, useState } from 'react';
import { Emoji } from 'emoji-mart';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { useApiHandlers } from 'lib/reader';
import { Button, ModalEventOnMouseDown, User } from 'components/common';
import { Dropdown } from 'components/common/dropdown';
import { Icon } from 'components/common/icons';

import type { Reaction, User as UserProps, UserReactions } from 'shared-types';

type Emoji = {
  type: Reaction;
  count: number;
  userIdentifiers: string[];
  userIds: number[];
  users: UserProps[];
};

type ReactionsData = {
  [key in Reaction]: {
    count: number;
    userIdentifiers: string[];
    userIds: number[];
    users: UserProps[];
  };
};

const mapReactions = (reactions: UserReactions) => {
  const data: ReactionsData = {
    ':+1:': { count: 0, userIdentifiers: [], userIds: [], users: [] },
    ':clap:': { count: 0, userIdentifiers: [], userIds: [], users: [] },
    ':smile:': { count: 0, userIdentifiers: [], userIds: [], users: [] },
    ':thinking_face:': {
      count: 0,
      userIdentifiers: [],
      userIds: [],
      users: []
    },
    ':heart:': { count: 0, userIdentifiers: [], userIds: [], users: [] },
    ':bulb:': { count: 0, userIdentifiers: [], userIds: [], users: [] },
    ':fire:': { count: 0, userIdentifiers: [], userIds: [], users: [] }
  };

  Object.keys(reactions).forEach((userId) => {
    const userData: Reaction[] = reactions[userId].reactions;
    userData.forEach((reaction) => {
      if (!data[reaction]) return;
      data[reaction].count += 1;
      data[reaction].userIdentifiers.push(reactions[userId].identifier);
      data[reaction].userIds.push(+userId);
      data[reaction].users.push(reactions[userId].user);
    });
  });

  return data;
};

const RenderBadge = ({ emoji }: { emoji: Emoji }) => (
  <div style={{ alignItems: 'flex-start', display: 'flex', gap: 5 }}>
    <div
      dangerouslySetInnerHTML={{
        __html: `${Emoji({
          emoji: emoji.type,
          html: true,
          set: 'apple',
          size: 15
        })}`
      }}
    />

    {emoji.count ? (
      <div className="count" style={{ fontSize: '1.2em' }}>
        {emoji.count}
      </div>
    ) : null}
  </div>
);

interface Props {
  commentId: number;
  commentReactions: UserReactions;
  isChildComment: boolean;
}

const ReaderBlockDiscussionCommentReactions = ({
  commentId,
  commentReactions = {},
  isChildComment
}: Props) => {
  const {
    messages,
    pageState: { readerCourseData },
    serverConfig: { userUseName },
    theme,
    user
  } = useReduxState();

  const { onUserReaction } = useApiHandlers();

  const updateReactionsTimeout = useRef<NodeJS.Timeout>(null);

  const [reactions, setReactions] = useState<ReactionsData>(
    mapReactions(commentReactions)
  );
  const [userReactions, setUserReactions] = useState<Reaction[]>(
    (user && commentReactions[user.id]?.reactions) || []
  );

  const emojis = Object.keys(reactions).map((key: Reaction) => (
    <RenderBadge
      emoji={{
        type: key,
        count: reactions[key].count,
        userIdentifiers: reactions[key].userIdentifiers,
        userIds: reactions[key].userIds,
        users: reactions[key].users
      }}
    />
  ));

  let userIdentifier: string;
  if (user)
    userIdentifier =
      userUseName && user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : user.username;

  useEffect(() => {
    return () => clearTimeout(updateReactionsTimeout.current);
  }, []);

  const handleClick = ({ emoji }: { emoji: Emoji }) => {
    const { type } = emoji;
    const userData = [...userReactions];
    const userIndex = userData.indexOf(type);

    if (userIndex !== -1) {
      const identifierIndex = emoji.userIdentifiers.indexOf(userIdentifier);
      const idIndex = emoji.userIds.indexOf(user.id);
      const usersIndex = emoji.users.indexOf(
        emoji.users.find((u) => u.id === user.id)
      );

      emoji.count -= 1;
      emoji.userIdentifiers.splice(identifierIndex, 1);
      emoji.userIds.splice(idIndex, 1);
      emoji.users.splice(usersIndex, 1);
      userData.splice(userIndex, 1);
    } else {
      emoji.count += 1;
      emoji.userIdentifiers.push(userIdentifier);
      emoji.userIds.push(user.id);
      emoji.users.push({
        email: user.email,
        firstName: user.firstName,
        id: user.id,
        lastName: user.lastName,
        username: user.username
      } as UserProps);
      userData.push(type);
    }

    setUserReactions(userData);
    updateReactionsData(userData);

    setReactions({
      ...reactions,
      [type]: {
        count: emoji.count,
        userIdentifiers: emoji.userIdentifiers,
        userIds: emoji.userIds,
        users: emoji.users
      }
    });
  };

  const updateReactionsData = (data: Reaction[]) => {
    if (!onUserReaction) return;

    clearTimeout(updateReactionsTimeout.current);
    updateReactionsTimeout.current = setTimeout(async () => {
      const reactions = await onUserReaction({
        blockId: commentId,
        blockType: 'discussion',
        reactions: data
      });
      if (reactions) setReactions(mapReactions(reactions));
    }, 1000);
  };

  const [emojisWithCount, emojisWithoutCount] = emojis.reduce(
    (emojiArr, emoji) => {
      emojiArr[emoji.props.emoji.count ? 0 : 1].push(emoji);
      return emojiArr;
    },
    [[], []]
  );

  const isPrivilegedUser =
    readerCourseData?.isCourseOwner ||
    readerCourseData?.isCourseAdmin ||
    readerCourseData?.isSuperAdmin;

  return (
    <div className="reactions-wrapper">
      {emojisWithCount.map((emoji) => {
        const { type, userIdentifiers, users } = emoji.props.emoji;

        const hasExtendedView = userIdentifiers.length > 5;

        const tooltip = !hasExtendedView
          ? userIdentifiers.sort().join(', ')
          : `${userIdentifiers
              .sort()
              .slice(0, 5)
              .join(', ')}, ${getMessageWithPlaceholders({
              message: messages.readerUserReactionsNumber,
              placeholders: { numberOfUsers: userIdentifiers.length - 5 }
            })}`;

        const ariaLabel =
          getMessageWithPlaceholders({
            message: userReactions.includes(type)
              ? messages.readerUserReactionsRemoveReaction
              : messages.readerUserReactionsReactWith,
            placeholders: { emoji: type.replace(':', '').replace(':', '') }
          }) + ` ${tooltip}`;

        return (
          <div id={`${type}-${commentId}`} key={`active-${type}-${commentId}`}>
            <Button
              aria-label={ariaLabel}
              category={isChildComment ? 'light' : 'lighter'}
              data-tooltip={tooltip}
              onClick={() => handleClick(emoji.props)}
              size="small"
              style={{ borderRadius: 7, lineHeight: 1 }}
              text={
                <>
                  {emoji}
                  {userReactions.includes(type) ? (
                    <div className="reaction-selected" />
                  ) : null}
                </>
              }
            />

            {hasExtendedView ? (
              <ModalEventOnMouseDown
                style={{ padding: 0 }}
                target={`${type}-${commentId}`}
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {getMessageWithPlaceholders({
                      message: messages.readerUserReactionsReaction,
                      placeholders: { emoji: '' }
                    })}
                    <div
                      dangerouslySetInnerHTML={{
                        __html: `&nbsp;${Emoji({
                          emoji: type,
                          html: true,
                          set: 'apple',
                          size: 20
                        })}`
                      }}
                    />
                  </div>
                }
              >
                {users.map((user) => (
                  <div key={user.id} className="reactions-user">
                    <User
                      enableProfileLink={isPrivilegedUser}
                      showEmail={isPrivilegedUser}
                      showEmailTooltip={false}
                      user={user}
                    />
                  </div>
                ))}
              </ModalEventOnMouseDown>
            ) : null}
          </div>
        );
      })}

      {emojisWithoutCount.length ? (
        <Dropdown
          align="left"
          buttonStyle={{ borderRadius: 7, lineHeight: 1 }}
          category={isChildComment ? 'light' : 'lighter'}
          contentStyle={{ display: 'flex', padding: 3 }}
          size="small"
          showArrow={false}
          styleContentButtons={false}
          title={
            <div className="dropdown-title">
              <div className="dropdown-title-icon">
                <Icon icon={['far', 'smile']} />
              </div>

              <div className="dropdown-title-plus">
                <Icon icon="plus" />
              </div>
            </div>
          }
          tooltip={messages.readerDiscussionAddReaction}
        >
          {emojisWithoutCount.map((emoji) => (
            <Button
              category="lighter"
              customBackgroundColor="transparent"
              key={`inactive-${emoji.props.emoji.type}-${commentId}`}
              onClick={() => handleClick(emoji.props)}
              size="small"
              text={emoji}
            />
          ))}
        </Dropdown>
      ) : null}

      <style jsx>{`
        .dropdown-title {
          position: relative;
        }
        .dropdown-title-icon {
          font-size: 1.5rem;
        }
        .dropdown-title-plus {
          background-color: ${isChildComment
            ? theme.lightGreyDark
            : theme.lightGrey};
          font-size: 0.8rem;
          line-height: 0;
          padding: 1px;
          position: absolute;
          top: -2px;
          right: -5px;
        }
        .reactions-wrapper {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 3px;
        }
        .reaction-selected {
          border: 2px solid ${theme.lightGreyDarkActive};
          border-radius: 7px;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }
        .reactions-user {
          padding: 10px 15px;
        }
        .reactions-user:not(:first-child) {
          border-top: 1px solid ${theme.lightGreyLighter};
        }
      `}</style>
    </div>
  );
};

export default ReaderBlockDiscussionCommentReactions;
